import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { api, mondayOf, todayISO, toLocalISODate, parseLocalDate, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Barbell, Trophy, UsersThree, CalendarBlank, ChartLine, Camera, X, ClockCounterClockwise } from "@phosphor-icons/react";
import QuickLogDialog from "@/components/blocks/QuickLogDialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import BodyPartHeatmap from "@/components/app/BodyPartHeatmap";

function GoToDatePill() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(() => parseLocalDate(todayISO()));

  const jump = (dateObj) => {
    if (!dateObj) return;
    const iso = toLocalISODate(dateObj);
    setSelected(dateObj);
    setOpen(false);
    nav(`/week?date=${iso}`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-moss/30 bg-white hover:bg-sage/40 text-moss text-sm font-medium transition-colors shadow-sm"
          data-testid="goto-date-pill"
        >
          <CalendarBlank size={16} weight="bold" />
          <span>Go to date</span>
          <span className="text-ink-3 text-xs hidden sm:inline">· {formatDate(toLocalISODate(selected), { month: "short", day: "numeric" })}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-2" data-testid="goto-date-popover">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={jump}
          initialFocus
          data-testid="goto-date-calendar"
        />
        <div className="flex items-center justify-between px-2 pb-2 pt-1 gap-2">
          <input
            type="date"
            defaultValue={toLocalISODate(selected)}
            onChange={(e) => {
              if (!e.target.value) return;
              jump(parseLocalDate(e.target.value));
            }}
            className="h-8 rounded-md border border-line px-2 text-sm text-ink bg-white flex-1"
            data-testid="goto-date-input"
          />
          <Button
            variant="ghost"
            className="rounded-lg h-8 px-3 text-xs"
            onClick={() => jump(parseLocalDate(todayISO()))}
            data-testid="goto-date-today"
          >
            Today
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ProgressReminderBanner({ user }) {
  const [visible, setVisible] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const today = new Date();
    // Show during the first 7 days of the month only.
    if (today.getDate() > 7) return;
    const monthKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
    const dismissKey = `progress-reminder-dismissed-${user.id}-${monthKey}`;
    if (localStorage.getItem(dismissKey)) return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/progression-photos", { params: { user_id: user.id } });
        if (cancelled) return;
        const hasThisMonth = data.some((p) => p.month === monthKey);
        if (!hasThisMonth) setVisible(true);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [user.id]);

  if (!visible) return null;

  const dismiss = () => {
    const today = new Date();
    const monthKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
    localStorage.setItem(`progress-reminder-dismissed-${user.id}-${monthKey}`, "1");
    setVisible(false);
  };

  return (
    <div className="rounded-2xl bg-sage/50 border border-moss/30 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
         data-testid="progress-reminder-banner">
      <div className="flex items-start sm:items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-moss grid place-items-center text-white shrink-0">
          <Camera size={18} weight="fill" />
        </div>
        <div>
          <div className="hs-label text-[10px] text-moss">First-of-month check-in</div>
          <div className="font-medium text-ink mt-0.5">Time for this month's progress photos.</div>
          <div className="text-sm text-ink-2 mt-0.5">Front, side, back — takes 60 seconds and pays off in 3 months.</div>
        </div>
      </div>
      <div className="flex items-center gap-2 self-end sm:self-auto">
        <Button onClick={() => nav("/progress")}
                className="rounded-xl bg-moss hover:bg-moss-hover text-white hover:text-white"
                data-testid="reminder-go-progress">
          Upload now <ArrowRight size={14} className="ml-1" />
        </Button>
        <Button variant="ghost" onClick={dismiss} className="rounded-xl px-2"
                aria-label="Dismiss" data-testid="reminder-dismiss">
          <X size={14} />
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="hs-card p-5">
      <div className="hs-label">{label}</div>
      <div className="hs-stat mt-2" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</div>
      {sub && <div className="text-xs text-ink-3 mt-1">{sub}</div>}
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [athletes, setAthletes] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [summary, setSummary] = useState(null);
  const [quickLogOpen, setQuickLogOpen] = useState(false);

  useEffect(() => {
    async function run() {
      if (user.role === "admin" || user.role === "coach") {
        const [a, c] = await Promise.all([
          api.get("/users", { params: { role: "athlete" } }),
          user.role === "admin"
            ? api.get("/users", { params: { role: "coach" } })
            : Promise.resolve({ data: [] }),
        ]);
        setAthletes(a.data.filter((u) => u.is_active !== false));
        setCoaches(c.data);
      } else if (user.role === "athlete") {
        const ws = mondayOf();
        try {
          const { data } = await api.get(`/stats/weekly-summary/${user.id}`, { params: { week_start: ws } });
          setSummary(data);
        } catch {/* empty */}
      }
    }
    run();
  }, [user]);

  if (user.role === "athlete") {
    return (
      <div className="p-6 sm:p-10 max-w-5xl mx-auto space-y-8">
        <div className="animate-fade-up">
          <div className="hs-label">Dashboard</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold mt-2">Hello, {user.name.split(" ")[0]}.</h1>
          <p className="text-ink-2 mt-2 max-w-xl">
            Ready to move? Your weekly program is waiting. Track every set, every rep — own your longevity.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <GoToDatePill />
          </div>
        </div>

        <ProgressReminderBanner user={user} />

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat label="This Week Volume" value={summary ? `${Math.round(summary.total_volume).toLocaleString()}` : "—"} sub="lbs · kg × reps" />
          <Stat label="Calories" value={summary ? Math.round(summary.total_calories || 0).toLocaleString() : "—"} sub="burned this week" />
          <Stat label="Sessions" value={summary?.sessions_completed ?? "—"} sub="days trained" />
          <Stat label="Sets Logged" value={summary?.sets_logged ?? "—"} />
          <Stat label="PRs Hit" value={summary?.prs_achieved ?? "—"} sub="this week" />
        </div>

        <BodyPartHeatmap athleteId={user.id} />

        <div className="grid md:grid-cols-3 gap-4">
          <Card className="hs-card p-6 hover:-translate-y-1 transition-all duration-200">
            <CalendarBlank size={28} className="text-terracotta" />
            <h3 className="font-heading text-xl font-semibold mt-3">My Week</h3>
            <p className="text-sm text-ink-2 mt-1">Log today's movements, sets, reps and metrics.</p>
            <Button onClick={() => nav("/week")} className="mt-4 bg-moss hover:bg-moss-hover text-white hover:text-white rounded-xl" data-testid="goto-week-button">
              Open workout <ArrowRight size={14} className="ml-1" />
            </Button>
          </Card>
          <Card className="hs-card p-6 hover:-translate-y-1 transition-all duration-200">
            <Trophy size={28} className="text-terracotta" />
            <h3 className="font-heading text-xl font-semibold mt-3">Records</h3>
            <p className="text-sm text-ink-2 mt-1">Your personal best lifts, times and distances.</p>
            <Button onClick={() => nav("/records")} variant="secondary" className="mt-4 rounded-xl" data-testid="goto-records-button">
              View PRs <ArrowRight size={14} className="ml-1" />
            </Button>
          </Card>
          <Card className="hs-card p-6 hover:-translate-y-1 transition-all duration-200">
            <ChartLine size={28} className="text-terracotta" />
            <h3 className="font-heading text-xl font-semibold mt-3">History</h3>
            <p className="text-sm text-ink-2 mt-1">Every set you've ever completed, searchable and clean.</p>
            <Button onClick={() => nav("/history")} variant="secondary" className="mt-4 rounded-xl" data-testid="goto-history-button">
              Browse <ArrowRight size={14} className="ml-1" />
            </Button>
          </Card>
        </div>

        <Card className="hs-card p-6 hover:-translate-y-1 transition-all duration-200 border-terracotta/30 bg-terracotta/5">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-terracotta grid place-items-center text-white shrink-0">
              <ClockCounterClockwise size={22} weight="bold" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-xl font-semibold">Traveling or training on your own?</h3>
              <p className="text-sm text-ink-2 mt-1">
                On vacation or hitting an open-gym session? Log the workout the moment you finish — or head to <button type="button" onClick={() => nav("/week")} className="underline underline-offset-2 text-terracotta hover:text-terracotta-hover font-semibold" data-testid="home-self-program-link">My Week</button> and program the full session for yourself with timers, schemes, and rest — no coach programming required.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  onClick={() => setQuickLogOpen(true)}
                  className="rounded-xl bg-terracotta hover:bg-terracotta-hover text-white hover:text-white"
                  data-testid="open-quick-log-button"
                >
                  <ClockCounterClockwise size={14} className="mr-2" /> Log past workout
                </Button>
                <Button
                  onClick={() => nav("/week")}
                  variant="secondary"
                  className="rounded-xl"
                  data-testid="home-build-own-week-button"
                >
                  Build my own week <ArrowRight size={14} className="ml-1" />
                </Button>
              </div>
            </div>
          </div>
        </Card>
        <QuickLogDialog open={quickLogOpen} onOpenChange={setQuickLogOpen} />
      </div>
    );
  }

  // Admin / Coach
  return (
    <div className="p-6 sm:p-10 max-w-7xl mx-auto space-y-8">
      <div className="animate-fade-up">
        <div className="hs-label">Coach dashboard</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold mt-2">
          {user.role === "admin" ? "The floor is yours." : "Let's get your athletes moving."}
        </h1>
        <p className="text-ink-2 mt-2 max-w-xl">
          Build weekly programs, assign movements, and watch your athletes unlock new records every week.
        </p>
      </div>

      <ProgressReminderBanner user={user} />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="Athletes" value={athletes.length} />
        {user.role === "admin" && <Stat label="Coaches" value={coaches.length} />}
        <Stat label="Your Role" value={user.role} />
      </div>

      <div className="hs-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="hs-label">Athlete roster</div>
            <h2 className="font-heading text-2xl font-semibold">Jump into a program</h2>
          </div>
          <Link to="/people" data-testid="open-people-link">
            <Button variant="secondary" className="rounded-xl">
              <UsersThree size={16} className="mr-2" /> Manage people
            </Button>
          </Link>
        </div>
        <div className="mt-5 divide-y divide-line">
          {athletes.length === 0 && (
            <div className="py-10 text-center">
              <Barbell size={32} className="mx-auto text-ink-3" />
              <div className="mt-3 text-ink-2">No athletes yet. Add one to get started.</div>
              <Link to="/people"><Button className="mt-4 rounded-xl bg-terracotta hover:bg-terracotta-hover text-white hover:text-white" data-testid="empty-add-athlete">Add an athlete</Button></Link>
            </div>
          )}
          {athletes.map((a) => (
            <div key={a.id} className="py-3 flex items-center justify-between group" data-testid={`athlete-row-${a.id}`}>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-sage grid place-items-center text-moss font-semibold">
                  {a.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                </div>
                <div>
                  <div className="font-medium">{a.name}</div>
                  <div className="text-xs text-ink-3">{a.email}</div>
                </div>
              </div>
              <Link to={`/athletes/${a.id}/week`}>
                <Button variant="ghost" className="rounded-xl text-moss hover:bg-sage hover:text-moss-hover" data-testid={`open-athlete-${a.id}`}>
                  Open program <ArrowRight size={14} className="ml-1" />
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </div>

      {/* Coaches train too — backdated personal workout entry sits below the roster. */}
      <Card className="hs-card p-6 hover:-translate-y-1 transition-all duration-200 border-terracotta/30 bg-terracotta/5">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-terracotta grid place-items-center text-white shrink-0">
            <ClockCounterClockwise size={22} weight="bold" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-xl font-semibold">Log a past workout (yours)</h3>
            <p className="text-sm text-ink-2 mt-1">
              Backdate any workout you trained on your own — Murph, an open-gym MetCon, a long bike ride — and it counts toward your records.
            </p>
            <Button
              onClick={() => setQuickLogOpen(true)}
              className="mt-4 rounded-xl bg-terracotta hover:bg-terracotta-hover text-white hover:text-white"
              data-testid="open-quick-log-button"
            >
              <ClockCounterClockwise size={14} className="mr-2" /> Log past workout
            </Button>
          </div>
        </div>
      </Card>
      <QuickLogDialog open={quickLogOpen} onOpenChange={setQuickLogOpen} />
    </div>
  );
}
